import React, { useEffect, useState, useRef, lazy } from 'react';
import { useChat, QUICK_REPLIES, EmployeeInfo } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { Search, Send, Building2, MessageCircle, CheckCheck, ArrowLeft, Paperclip, X, FileText, Download, Trash2, Loader2, CheckCircle2, RotateCcw, Zap, MoreVertical, Filter, Plus, Users } from 'lucide-react';
import { useDocumentTitle } from '../utils/useDocumentTitle';
const FILE_SIZE_LIMIT = 5 * 1024 * 1024; // 5MB
const ACCEPTED_TYPES = 'image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv';
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
function isImageType(type?: string): boolean {
  return !!type && type.startsWith('image/');
}
function FileAttachment({
  msg,
  isMe
}: {
  msg: any;
  isMe: boolean;
}) {
  if (!msg.fileUrl) return null;
  if (isImageType(msg.fileType)) {
    return <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className="block mt-2" data-id="element-1256">
        <img src={msg.fileUrl} alt={msg.fileName || 'Imagen'} className="max-w-[280px] max-h-[200px] rounded-xl object-cover border border-gray-200/50" loading="lazy" data-id="element-1257" />
      </a>;
  }
  return <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 mt-2 p-3 rounded-xl border transition-colors ${isMe ? 'bg-white/10 border-white/20 hover:bg-white/15' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`} data-id="element-1258">
      <div className={`p-2 rounded-lg ${isMe ? 'bg-white/15' : 'bg-gray-200'}`} data-id="element-1259">
        <FileText size={18} className={isMe ? 'text-white' : 'text-gray-600'} data-id="element-1260" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-1261">
        <p className={`text-xs font-medium truncate ${isMe ? 'text-white' : 'text-gray-900'}`} data-id="element-1262">
          {msg.fileName || 'Archivo'}
        </p>
        {msg.fileSize && <p className={`text-[10px] ${isMe ? 'text-gray-300' : 'text-gray-500'}`} data-id="element-1263">
            {formatFileSize(msg.fileSize)}
          </p>}
      </div>
      <Download size={14} className={isMe ? 'text-gray-300' : 'text-gray-400'} data-id="element-1264" />
    </a>;
}
type FilterType = 'all' | 'unread' | 'closed';
export function AdminChatPage() {
  useDocumentTitle('Chat de Soporte | Admin');
  const {
    user
  } = useAuth();
  const {
    conversations,
    messages,
    sendMessage,
    markAsRead,
    deleteConversation,
    closeConversation,
    reopenConversation,
    startConversation,
    getEmployeeList,
    setTyping,
    typingIn,
    loading,
    uploading
  } = useChat();
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [employeeList, setEmployeeList] = useState<EmployeeInfo[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeInfo | null>(null);
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const quickRepliesRef = useRef<HTMLDivElement>(null);
  const justSentMessage = useRef(false);
  const prevConversationId = useRef<string | null>(null);
  const selectedConversation = conversations.find(c => c.id === selectedConversationId);
  const currentMessages = messages.filter(m => m.conversationId === selectedConversationId);
  // Close quick replies when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (quickRepliesRef.current && !quickRepliesRef.current.contains(event.target as Node)) {
        setShowQuickReplies(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Mark as read when selecting a conversation
  useEffect(() => {
    if (selectedConversationId && selectedConversationId !== prevConversationId.current) {
      prevConversationId.current = selectedConversationId;
      markAsRead(selectedConversationId);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'auto'
        });
      }, 50);
    }
  }, [selectedConversationId]);
  // Scroll to bottom only when user sends a message
  useEffect(() => {
    if (justSentMessage.current) {
      justSentMessage.current = false;
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: 'smooth'
        });
      }, 150);
    }
  }, [currentMessages.length]);
  // Mark new incoming messages as read (without scrolling)
  useEffect(() => {
    if (selectedConversationId) {
      const hasUnread = currentMessages.some(m => m.senderRole === 'employee' && !m.read);
      if (hasUnread) markAsRead(selectedConversationId);
    }
  }, [currentMessages.length, selectedConversationId]);
  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newMessage.trim() && !selectedFile || !selectedConversationId) return;
    justSentMessage.current = true;
    sendMessage(newMessage, selectedConversationId, selectedFile || undefined);
    setNewMessage('');
    clearFile();
    inputRef.current?.focus();
  };
  const handleQuickReply = (text: string) => {
    if (!selectedConversationId) return;
    justSentMessage.current = true;
    sendMessage(text, selectedConversationId);
    setShowQuickReplies(false);
    inputRef.current?.focus();
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > FILE_SIZE_LIMIT) {
      alert('El archivo es muy grande. Máximo 5MB.');
      return;
    }
    setSelectedFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setFilePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };
  const clearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const handleSelectConversation = (convId: string) => {
    setSelectedConversationId(convId);
    setMobileShowChat(true);
  };
  const handleDeleteConversation = () => {
    if (selectedConversationId) {
      deleteConversation(selectedConversationId);
      setSelectedConversationId(null);
      setMobileShowChat(false);
      setShowDeleteConfirm(false);
    }
  };
  const handleToggleStatus = () => {
    if (!selectedConversation) return;
    if (selectedConversation.status === 'closed') {
      reopenConversation(selectedConversation.id);
    } else {
      closeConversation(selectedConversation.id);
      // After resolving, go back to conversation list
      setSelectedConversationId(null);
      setMobileShowChat(false);
    }
  };
  const handleOpenNewChat = async () => {
    setShowNewChatModal(true);
    setEmployeeSearch('');
    setNewChatMessage('');
    setSelectedEmployee(null);
    setCompanyFilter('all');
    setBranchFilter('all');
    setLoadingEmployees(true);
    try {
      const employees = await getEmployeeList();
      setEmployeeList(employees);
    } catch (e) {
      console.error('Error loading employees:', e);
    }
    setLoadingEmployees(false);
  };
  const handleStartNewChat = () => {
    if (!selectedEmployee || !newChatMessage.trim()) return;
    startConversation(selectedEmployee, newChatMessage.trim());
    setShowNewChatModal(false);
    // Select the new conversation
    setTimeout(() => {
      setSelectedConversationId(selectedEmployee.id);
      setMobileShowChat(true);
    }, 100);
  };
  const filteredEmployees = employeeList.filter(emp => {
    const search = employeeSearch.toLowerCase();
    const matchesSearch = emp.name.toLowerCase().includes(search) || emp.company.toLowerCase().includes(search) || emp.branch.toLowerCase().includes(search);
    const matchesCompany = companyFilter === 'all' || emp.company === companyFilter;
    const matchesBranch = branchFilter === 'all' || emp.branch === branchFilter;
    return matchesSearch && matchesCompany && matchesBranch;
  });
  // Derive unique companies and branches for filters
  const uniqueCompanies = [...new Set(employeeList.map(e => e.company))].sort();
  const uniqueBranches = [...new Set(employeeList.filter(e => companyFilter === 'all' || e.company === companyFilter).map(e => e.branch))].sort();
  // Separate into new and existing conversations
  const employeesWithChat = filteredEmployees.filter(emp => conversations.some(c => c.id === emp.id));
  const employeesWithoutChat = filteredEmployees.filter(emp => !conversations.some(c => c.id === emp.id));
  const filteredConversations = conversations.filter(c => {
    const matchesSearch = c.participantName.toLowerCase().includes(searchTerm.toLowerCase()) || c.participantCompany.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (filter === 'unread') return c.unreadCount > 0;
    if (filter === 'closed') return c.status === 'closed';
    // 'all' shows everything (open and closed)
    return true;
  });
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
    return date.toLocaleDateString('es-DO', {
      day: 'numeric',
      month: 'short'
    });
  };
  // Group messages by date
  const groupedMessages: {
    date: string;
    messages: typeof currentMessages;
  }[] = [];
  let currentDate = '';
  currentMessages.forEach(msg => {
    const msgDate = new Date(msg.timestamp).toLocaleDateString('es-DO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groupedMessages.push({
        date: msgDate,
        messages: []
      });
    }
    groupedMessages[groupedMessages.length - 1].messages.push(msg);
  });
  return <div className="fixed inset-0 top-16 lg:top-0 lg:left-64 flex bg-white overflow-hidden z-10" data-id="element-1265">
      {/* Sidebar - Conversations List */}
      <div className={`w-full lg:w-96 border-r border-gray-200 flex flex-col bg-white ${mobileShowChat ? 'hidden lg:flex' : 'flex'}`} data-id="element-1266">
        <div className="p-4 border-b border-gray-100 bg-white shrink-0 space-y-3" data-id="element-1267">
          <div className="flex items-center justify-between" data-id="element-1268">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-1269">Mensajes</h2>
            <div className="flex items-center gap-2" data-id="element-1270">
              {conversations.length > 0 && <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full" data-id="element-1271">
                  {conversations.length}
                </span>}
              <button onClick={handleOpenNewChat} className="p-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors active:scale-95" title="Nuevo chat" data-id="element-1272">
                <Plus size={16} data-id="element-1273" />
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex p-1 bg-gray-100 rounded-xl" data-id="element-1274">
            <button onClick={() => setFilter('all')} className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${filter === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1275">
              Todos
            </button>
            <button onClick={() => setFilter('unread')} className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${filter === 'unread' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1276">
              No leídos
            </button>
            <button onClick={() => setFilter('closed')} className={`flex-1 py-1.5 text-xs font-medium rounded-lg transition-all ${filter === 'closed' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1277">
              Resueltos
            </button>
          </div>

          <div className="relative" data-id="element-1278">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1279" />
            <input type="text" placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all" data-id="element-1280" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0" data-id="element-1281">
          {loading ? <div className="p-8 text-center" data-id="element-1282">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-3" data-id="element-1283"></div>
              <p className="text-sm text-gray-500" data-id="element-1284">Cargando chats...</p>
            </div> : filteredConversations.length === 0 ? <div className="p-8 text-center text-gray-500" data-id="element-1285">
              <MessageCircle size={40} className="mx-auto mb-3 opacity-20" data-id="element-1286" />
              <p className="text-sm font-medium" data-id="element-1287">No hay conversaciones</p>
              <p className="text-xs mt-1" data-id="element-1288">
                {filter === 'unread' ? 'No tienes mensajes sin leer' : filter === 'closed' ? 'No hay conversaciones resueltas' : 'Los mensajes aparecerán aquí'}
              </p>
            </div> : filteredConversations.map(conv => <button key={conv.id} onClick={() => handleSelectConversation(conv.id)} className={`w-full p-4 text-left hover:bg-gray-50 transition-colors flex gap-3 border-b border-gray-50 ${selectedConversationId === conv.id ? 'bg-gray-50' : ''} ${conv.status === 'closed' ? 'opacity-75' : ''}`} data-id="element-1289">
                <div className="relative shrink-0" data-id="element-1290">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-sm ${selectedConversationId === conv.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700'}`} data-id="element-1291">
                    {conv.participantName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  {conv.unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white" data-id="element-1292">
                      {conv.unreadCount}
                    </span>}
                  {conv.status === 'closed' && conv.unreadCount === 0 && <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-gray-500 text-white rounded-full flex items-center justify-center border-2 border-white" data-id="element-1293">
                      <CheckCircle2 size={10} data-id="element-1294" />
                    </span>}
                </div>
                <div className="flex-1 min-w-0" data-id="element-1295">
                  <div className="flex justify-between items-center mb-0.5" data-id="element-1296">
                    <h3 className={`text-sm truncate ${conv.unreadCount > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`} data-id="element-1297">
                      {conv.participantName}
                    </h3>
                    {conv.lastTimestamp && <span className={`text-[10px] whitespace-nowrap ml-2 ${conv.unreadCount > 0 ? 'text-emerald-600 font-bold' : 'text-gray-400'}`} data-id="element-1298">
                        {formatTime(conv.lastTimestamp)}
                      </span>}
                  </div>
                  {conv.participantCompany && <p className="text-[10px] text-gray-400 flex items-center gap-1 mb-0.5" data-id="element-1299">
                      <Building2 size={10} data-id="element-1300" /> {conv.participantCompany}
                    </p>}
                  <div className="flex items-center gap-1.5" data-id="element-1301">
                    {conv.status === 'closed' && <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-medium border border-gray-200" data-id="element-1302">
                        Resuelto
                      </span>}
                    <p className={`text-xs truncate flex-1 ${conv.unreadCount > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`} data-id="element-1303">
                      {conv.lastMessage || 'Nueva conversación'}
                    </p>
                  </div>
                </div>
              </button>)}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={`flex-1 flex flex-col bg-white overflow-hidden ${!mobileShowChat ? 'hidden lg:flex' : 'flex'}`} data-id="element-1304">
        {selectedConversation ? <>
            {/* Chat Header */}
            <div className="h-16 border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 bg-white shrink-0" data-id="element-1305">
              <div className="flex items-center gap-3" data-id="element-1306">
                <button onClick={() => setMobileShowChat(false)} className="lg:hidden p-2 -ml-2 mr-1 rounded-xl hover:bg-gray-100 text-gray-600" data-id="element-1307">
                  <ArrowLeft size={20} data-id="element-1308" />
                </button>
                <div className="w-10 h-10 rounded-full bg-gray-900 text-white flex items-center justify-center font-bold text-sm" data-id="element-1309">
                  {selectedConversation.participantName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div data-id="element-1310">
                  <h2 className="font-bold text-gray-900 flex items-center gap-2" data-id="element-1311">
                    {selectedConversation.participantName}
                    {selectedConversation.status === 'closed' && <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] font-medium border border-gray-200 flex items-center gap-1" data-id="element-1312">
                        <CheckCircle2 size={10} data-id="element-1313" /> Resuelto
                      </span>}
                  </h2>
                  {selectedConversation.participantCompany && <div className="flex items-center gap-1.5 text-xs text-gray-500" data-id="element-1314">
                      <Building2 size={12} data-id="element-1315" />
                      <span data-id="element-1316">{selectedConversation.participantCompany}</span>
                    </div>}
                </div>
              </div>

              {/* Admin actions */}
              <div className="flex items-center gap-1" data-id="element-1317">
                <button onClick={handleToggleStatus} className={`p-2 rounded-xl transition-colors flex items-center gap-2 text-sm font-medium ${selectedConversation.status === 'closed' ? 'text-gray-600 hover:bg-gray-100' : 'text-emerald-600 hover:bg-emerald-50'}`} title={selectedConversation.status === 'closed' ? 'Reabrir conversación' : 'Marcar como resuelto'} data-id="element-1318">
                  {selectedConversation.status === 'closed' ? <>
                      <RotateCcw size={18} data-id="element-1319" />
                      <span className="hidden sm:inline" data-id="element-1320">Reabrir</span>
                    </> : <>
                      <CheckCircle2 size={18} data-id="element-1321" />
                      <span className="hidden sm:inline" data-id="element-1322">Resolver</span>
                    </>}
                </button>

                <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-1323"></div>

                <button onClick={() => setShowDeleteConfirm(true)} className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors" title="Eliminar conversación" data-id="element-1324">
                  <Trash2 size={18} data-id="element-1325" />
                </button>
              </div>
            </div>

            {/* Messages List */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-6 bg-gray-50/50 min-h-0" data-id="element-1326">
              {groupedMessages.map((group, gi) => <div key={gi} data-id="element-1327">
                  <div className="flex items-center justify-center mb-4" data-id="element-1328">
                    <span className="text-[10px] font-medium text-gray-400 bg-white px-3 py-1 rounded-full border border-gray-100 shadow-sm capitalize" data-id="element-1329">
                      {group.date}
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-1330">
                    {group.messages.map((msg, i) => {
                const isMe = msg.senderRole === 'admin';
                const showName = !isMe && (i === 0 || group.messages[i - 1]?.senderRole !== msg.senderRole);
                const showAdminName = isMe && (i === 0 || group.messages[i - 1]?.senderRole !== msg.senderRole);
                // Use conversation's participantName for employee messages (always up-to-date)
                const displayName = isMe ? msg.senderName : selectedConversation?.participantName || msg.senderName;
                return <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-id="element-1331">
                          <div className="max-w-[75%]" data-id="element-1332">
                            {(showName || showAdminName) && <p className={`text-[11px] font-semibold mb-1 ${isMe ? 'text-right text-gray-400 mr-1' : 'text-gray-500 ml-1'}`} data-id="element-1333">
                                {displayName}
                              </p>}
                            <div className={`px-4 py-3 rounded-2xl shadow-sm text-sm leading-relaxed ${isMe ? 'bg-gray-900 text-white rounded-tr-md' : 'bg-white text-gray-800 border border-gray-100 rounded-tl-md'}`} data-id="element-1334">
                              {msg.text && <p data-id="element-1335">{msg.text}</p>}
                              <FileAttachment msg={msg} isMe={isMe} data-id="element-1336" />
                              <div className={`flex items-center gap-1 mt-1.5 ${isMe ? 'justify-end' : ''}`} data-id="element-1337">
                                <span className={`text-[10px] ${isMe ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-1338">
                                  {new Date(msg.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                                </span>
                                {isMe && msg.read && <CheckCheck size={12} className="text-emerald-400" data-id="element-1339" />}
                              </div>
                            </div>
                          </div>
                        </div>;
              })}
                  </div>
                </div>)}

              {selectedConversation.status === 'closed' && <div className="flex justify-center py-4" data-id="element-1340">
                  <div className="bg-gray-100 text-gray-500 text-xs px-4 py-2 rounded-full flex items-center gap-2 border border-gray-200" data-id="element-1341">
                    <CheckCircle2 size={14} data-id="element-1342" />
                    Esta conversación fue marcada como resuelta
                  </div>
                </div>}

              {/* Typing indicator */}
              {selectedConversation.status !== 'closed' && typingIn.has(selectedConversationId!) && <div className="flex justify-start" data-id="element-1343">
                    <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-md px-4 py-3 shadow-sm" data-id="element-1344">
                      <div className="flex items-center gap-1" data-id="element-1345">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-1346"></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-1347"></span>
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-1348"></span>
                      </div>
                    </div>
                  </div>}

              <div ref={messagesEndRef} data-id="element-1349" />
            </div>

            {/* File Preview */}
            {selectedFile && <div className="px-4 pt-3 bg-white border-t border-gray-100 shrink-0" data-id="element-1350">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200" data-id="element-1351">
                  {filePreview ? <img src={filePreview} alt="" className="w-12 h-12 rounded-lg object-cover" data-id="element-1352" /> : <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center" data-id="element-1353">
                      <FileText size={20} className="text-gray-500" data-id="element-1354" />
                    </div>}
                  <div className="flex-1 min-w-0" data-id="element-1355">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1356">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-1357">
                      {formatFileSize(selectedFile.size)}
                    </p>
                  </div>
                  <button onClick={clearFile} className="p-1.5 rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1358">
                    <X size={16} data-id="element-1359" />
                  </button>
                </div>
              </div>}

            {/* Input Area */}
            <div className="p-4 border-t border-gray-100 bg-white shrink-0 relative" data-id="element-1360">
              {/* Quick Replies Popover */}
              {showQuickReplies && <div ref={quickRepliesRef} className="absolute bottom-full left-4 mb-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-10 animate-in fade-in slide-in-from-bottom-2" data-id="element-1361">
                  <div className="p-2 bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-1362">
                    Respuestas rápidas
                  </div>
                  <div className="max-h-60 overflow-y-auto p-1" data-id="element-1363">
                    {QUICK_REPLIES.map((reply, i) => <button key={i} onClick={() => handleQuickReply(reply.text)} className="w-full text-left px-3 py-2.5 hover:bg-gray-50 rounded-lg text-sm text-gray-700 transition-colors group" data-id="element-1364">
                        <div className="font-medium text-gray-900 mb-0.5" data-id="element-1365">
                          {reply.label}
                        </div>
                        <div className="text-xs text-gray-500 truncate group-hover:text-gray-600" data-id="element-1366">
                          {reply.text}
                        </div>
                      </button>)}
                  </div>
                </div>}

              <form onSubmit={handleSend} className="flex items-center gap-2" data-id="element-1367">
                <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} onChange={handleFileSelect} className="hidden" data-id="element-1368" />

                <button type="button" onClick={() => fileInputRef.current?.click()} className="p-3 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0" title="Adjuntar archivo (máx. 5MB)" data-id="element-1369">
                  <Paperclip size={20} data-id="element-1370" />
                </button>

                <button type="button" onClick={() => setShowQuickReplies(!showQuickReplies)} className={`p-3 rounded-xl transition-colors shrink-0 ${showQuickReplies ? 'bg-gray-100 text-gray-900' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`} title="Respuestas rápidas" data-id="element-1371">
                  <Zap size={20} data-id="element-1372" />
                </button>

                <div className="relative flex-1" data-id="element-1373">
                  <input ref={inputRef} type="text" value={newMessage} onChange={e => {
                setNewMessage(e.target.value);
                if (selectedConversationId) setTyping(selectedConversationId);
              }} placeholder={selectedConversation.status === 'closed' ? 'Escribir reabrirá la conversación...' : 'Escribe un mensaje...'} className="w-full pl-5 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all text-sm" data-id="element-1374" />
                </div>
                <button type="submit" disabled={!newMessage.trim() && !selectedFile || uploading} className="p-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95 shrink-0" data-id="element-1375">
                  {uploading ? <Loader2 size={18} className="animate-spin" data-id="element-1376" /> : <Send size={18} data-id="element-1377" />}
                </button>
              </form>
            </div>
          </> : <div className="flex-1 flex flex-col items-center justify-center text-gray-400 bg-gray-50/50" data-id="element-1378">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4" data-id="element-1379">
              <MessageCircle size={36} className="opacity-30" data-id="element-1380" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-1381">
              Selecciona una conversación
            </h3>
            <p className="text-sm text-gray-500" data-id="element-1382">
              Elige un cliente de la lista para ver sus mensajes
            </p>
          </div>}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDeleteConfirm(false)} data-id="element-1383">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 border border-gray-200" onClick={e => e.stopPropagation()} data-id="element-1384">
            <div className="flex items-center gap-3 mb-4" data-id="element-1385">
              <div className="p-2 bg-red-100 rounded-xl" data-id="element-1386">
                <Trash2 size={20} className="text-red-600" data-id="element-1387" />
              </div>
              <div data-id="element-1388">
                <h3 className="font-bold text-gray-900" data-id="element-1389">
                  Eliminar conversación
                </h3>
                <p className="text-xs text-gray-500" data-id="element-1390">
                  Con {selectedConversation?.participantName}
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-6" data-id="element-1391">
              Se eliminarán todos los mensajes de esta conversación. Esta acción
              no se puede deshacer.
            </p>
            <div className="flex gap-3" data-id="element-1392">
              <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1393">
                Cancelar
              </button>
              <button onClick={handleDeleteConversation} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors" data-id="element-1394">
                Eliminar
              </button>
            </div>
          </div>
        </div>}

      {/* New Chat Modal */}
      {showNewChatModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowNewChatModal(false)} data-id="element-1395">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()} data-id="element-1396">
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-100 shrink-0" data-id="element-1397">
              <div className="flex items-center justify-between mb-1" data-id="element-1398">
                <div className="flex items-center gap-3" data-id="element-1399">
                  <div className="p-2 bg-gray-900 rounded-xl" data-id="element-1400">
                    <MessageCircle size={18} className="text-white" data-id="element-1401" />
                  </div>
                  <div data-id="element-1402">
                    <h3 className="font-bold text-gray-900" data-id="element-1403">
                      Nueva conversación
                    </h3>
                    <p className="text-xs text-gray-500" data-id="element-1404">
                      Selecciona un empleado para iniciar chat
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowNewChatModal(false)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-1405">
                  <X size={18} data-id="element-1406" />
                </button>
              </div>
            </div>

            {/* Step 1: Select Employee */}
            {!selectedEmployee ? <div className="flex flex-col min-h-0 flex-1" data-id="element-1407">
                <div className="p-4 shrink-0 space-y-3" data-id="element-1408">
                  <div className="relative" data-id="element-1409">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-1410" />
                    <input type="text" placeholder="Buscar empleado..." value={employeeSearch} onChange={e => setEmployeeSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all" autoFocus data-id="element-1411" />
                  </div>

                  {/* Company & Branch Filters */}
                  <div className="flex gap-2" data-id="element-1412">
                    <select value={companyFilter} onChange={e => {
                setCompanyFilter(e.target.value);
                setBranchFilter('all');
              }} className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-2 focus:ring-gray-900 focus:border-transparent" data-id="element-1413">
                      <option value="all" data-id="element-1414">Todas las empresas</option>
                      {uniqueCompanies.map(c => <option key={c} value={c} data-id="element-1415">
                          {c}
                        </option>)}
                    </select>
                    <select value={branchFilter} onChange={e => setBranchFilter(e.target.value)} className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700 focus:ring-2 focus:ring-gray-900 focus:border-transparent" data-id="element-1416">
                      <option value="all" data-id="element-1417">Todas las sucursales</option>
                      {uniqueBranches.map(b => <option key={b} value={b} data-id="element-1418">
                          {b}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0" data-id="element-1419">
                  {loadingEmployees ? <div className="py-8 text-center" data-id="element-1420">
                      <Loader2 size={24} className="animate-spin mx-auto mb-2 text-gray-400" data-id="element-1421" />
                      <p className="text-sm text-gray-500" data-id="element-1422">
                        Cargando empleados...
                      </p>
                    </div> : filteredEmployees.length === 0 ? <div className="py-8 text-center" data-id="element-1423">
                      <Users size={32} className="mx-auto mb-2 text-gray-300" data-id="element-1424" />
                      <p className="text-sm text-gray-500 font-medium" data-id="element-1425">
                        {employeeSearch || companyFilter !== 'all' || branchFilter !== 'all' ? 'No se encontraron empleados' : 'No hay empleados disponibles'}
                      </p>
                      {(companyFilter !== 'all' || branchFilter !== 'all') && <button onClick={() => {
                setCompanyFilter('all');
                setBranchFilter('all');
                setEmployeeSearch('');
              }} className="text-xs text-gray-500 underline mt-2 hover:text-gray-700" data-id="element-1426">
                          Limpiar filtros
                        </button>}
                    </div> : <div className="space-y-1" data-id="element-1427">
                      {employeesWithoutChat.length > 0 && <>
                          {employeesWithoutChat.map(emp => <button key={emp.id} onClick={() => setSelectedEmployee(emp)} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left" data-id="element-1428">
                              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-bold text-sm text-gray-700 shrink-0" data-id="element-1429">
                                {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-1430">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1431">
                                  {emp.name}
                                </p>
                                <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1432">
                                  <Building2 size={10} data-id="element-1433" /> {emp.company} ·{' '}
                                  {emp.branch}
                                </p>
                              </div>
                            </button>)}
                        </>}
                      {employeesWithChat.length > 0 && <>
                          <div className="pt-2 pb-1 px-1" data-id="element-1434">
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1435">
                              Con chat existente
                            </p>
                          </div>
                          {employeesWithChat.map(emp => <button key={emp.id} onClick={() => {
                  setShowNewChatModal(false);
                  handleSelectConversation(emp.id);
                }} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left" data-id="element-1436">
                              <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-bold text-sm text-white shrink-0" data-id="element-1437">
                                {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-1438">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1439">
                                  {emp.name}
                                </p>
                                <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1440">
                                  <Building2 size={10} data-id="element-1441" /> {emp.company} ·{' '}
                                  {emp.branch}
                                </p>
                              </div>
                              <span className="text-[10px] text-gray-400 font-medium px-2 py-0.5 bg-gray-100 rounded-full" data-id="element-1442">
                                Abrir chat
                              </span>
                            </button>)}
                        </>}
                    </div>}
                </div>
              </div> /* Step 2: Write Message */ : <div className="p-5 space-y-4" data-id="element-1443">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100" data-id="element-1444">
                  <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center font-bold text-sm text-white shrink-0" data-id="element-1445">
                    {selectedEmployee.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1446">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-1447">
                      {selectedEmployee.name}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1448">
                      <Building2 size={10} data-id="element-1449" /> {selectedEmployee.company} ·{' '}
                      {selectedEmployee.branch}
                    </p>
                  </div>
                  <button onClick={() => setSelectedEmployee(null)} className="text-xs text-gray-500 hover:text-gray-700 font-medium px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1450">
                    Cambiar
                  </button>
                </div>

                <div data-id="element-1451">
                  <label className="text-xs font-medium text-gray-500 mb-1.5 block" data-id="element-1452">
                    Mensaje inicial
                  </label>
                  <textarea value={newChatMessage} onChange={e => setNewChatMessage(e.target.value)} placeholder="Escribe el primer mensaje..." rows={3} className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all resize-none" autoFocus data-id="element-1453" />
                </div>

                {/* Quick message suggestions */}
                <div className="flex flex-wrap gap-1.5" data-id="element-1454">
                  {QUICK_REPLIES.slice(0, 3).map((reply, i) => <button key={i} onClick={() => setNewChatMessage(reply.text)} className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors" data-id="element-1455">
                      {reply.label}
                    </button>)}
                </div>

                <div className="flex gap-3 pt-2" data-id="element-1456">
                  <button onClick={() => setShowNewChatModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-1457">
                    Cancelar
                  </button>
                  <button onClick={handleStartNewChat} disabled={!newChatMessage.trim()} className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2" data-id="element-1458">
                    <Send size={14} data-id="element-1459" />
                    Enviar
                  </button>
                </div>
              </div>}
          </div>
        </div>}
    </div>;
}